import { useEffect, useState } from "react";
import { omitBy, isNil, isEmpty, last } from "lodash";
import normalize from "Utils/normalizer";
import useSession from "Hooks/session.hook";
import {
  listBatches,
  listBoxes,
  listTrolley,
} from "Services/delivery.services";
import { listSlotsData } from "Services/delivery-settings.services";
import { toaster } from "Helpers/index";
import { SortingMode } from "Types/common.types";
import { useRecoilState } from "recoil";
import { GlobalFilterAtom } from "Recoil/global_filter.atom";
import {
  fetchPublicSlotDataList,
  fetchPublicTrolleyList,
  fetchSSESubscription,
} from "Services/fullscreen.services";
import { TOPIC_FULLSCREEN } from "Types/topic.types";

const TROLLEY_STATUS = ["LOADING", "LOADED", "DISPATCHED"];

const noAccessCodes = [87700, 87800];

const useDeliveryFullscreen = (props?) => {
  const { isPublic = false } = props || {};
  const { org } = isPublic ? { org: null } : useSession();

  const [globalFilterState] = useRecoilState(GlobalFilterAtom);

  const initialFilterObj = isPublic
    ? {}
    : {
        companyOrgID:
          globalFilterState?.selectedOrgIDs?.length &&
          globalFilterState?.selectedOrgIDs?.length == 1
            ? globalFilterState?.selectedOrgIDs[0]
            : org._id,
      };
  const [filterObj, setFilterObj] = useState<any>(initialFilterObj);

  const [upcomingBatches, setUpcomingBatches] = useState<any>([]);
  const [racks, setRacks] = useState<any>([]);
  const [trolleys, setTrolleys] = useState<any>([]);
  const [boxesSlot, setBoxesSlot] = useState<any>([]);
  const [boxes, setBoxes] = useState<any>([]);
  const [accessForbidden, setAccessToForbidden] = useState<boolean>(false);
  const [trolleyServicePointID, setTrolleyServicePointID] = useState<string>();

  //  Other functions
  // =================================
  const fetchUpcomingBatches = async () => {
    let filters = omitBy(filterObj, isNil);
    const responseData = await listBatches(omitBy(filters, isNil));
    if (responseData && !responseData.error) {
      setUpcomingBatches(responseData);
    } else {
    }
  };

  const fetchBoxes = async (status?: string) => {
    const filter = omitBy(filterObj, isNil);
    let filters: any = {};

    if (!isEmpty(status)) {
      filters = { ...filter, status: [status] };
    }

    const responseData = await listBoxes(omitBy(filters, isNil));
    if (responseData && !responseData.error) {
      setBoxes(responseData);
    } else {
    }
  };

  const fetchSlotsData = async () => {
    const responseData = await listSlotsData(
      omitBy(
        {
          ...filterObj,
        },
        isNil,
      ),
    );
    if (responseData && !responseData.error) {
      setBoxesSlot(responseData);
    } else {
    }
  };

  const fetchTrolleys = async () => {
    let filters = omitBy({ status: TROLLEY_STATUS, ...filterObj }, isNil);
    const responseData = await listTrolley(omitBy(filters, isNil));
    if (responseData && !responseData.error) {
      setTrolleys(responseData);
    } else {
      toaster("error", responseData.error);
    }
  };

  //  Public functions
  // =================================
  const handleFetchPublicBoxList = async (id) => {
    const responseData = await fetchPublicSlotDataList(id);

    if (responseData && !responseData.error) {
      setBoxesSlot(responseData);
    } else {
      const { code } = responseData?.error;

      if (noAccessCodes.includes(code)) {
        console.log("no access");
        setAccessToForbidden(true);
      }
    }
  };

  const handleFetchPublicTrolleyList = async (id) => {
    const responseData = await fetchPublicTrolleyList(id);

    if (responseData && !responseData.error) {
      const batchParcels = responseData?.batchParcels?.batchParcels ?? [];
      const boxParcels = responseData?.boxParcels?.boxParcels ?? [];
      const trolleyParcels = responseData?.trolleyParcels?.trolleyParcels ?? [];

      setTrolleyServicePointID(responseData?.servicePointID);
      setUpcomingBatches(batchParcels);
      setBoxes(boxParcels);
      setTrolleys(trolleyParcels);
    } else {
      const { code } = responseData?.error;

      if (noAccessCodes.includes(code)) {
        console.log("no access");
        setAccessToForbidden(true);
      }
    }
  };

  useEffect(() => {
    let eventSource: EventSource | null = null;

    const setupSSE = async () => {
      try {
        eventSource = await fetchSSESubscription(isPublic);

        eventSource.onopen = (event: any) => {
          console.debug("SSE Connection open", event);
        };

        eventSource.onmessage = (event) => {
          const data = JSON.parse(event.data);
          const { message, topic } = data;

          console.debug("Response", data);

          switch (topic) {
            case TOPIC_FULLSCREEN.RACK:
              if (isPublic && location.pathname.includes("rack")) {
                const normalizedPublicSlot = message.racks.map((rack: any) =>
                  normalize.response.public_fullscreen(rack),
                );

                // public users only see one rack which is determined
                // first via getrackslot endpoint
                setBoxesSlot((prev: any) =>
                  normalizedPublicSlot.filter(
                    (slot: any) => slot._id === prev[0]._id,
                  ),
                );
                break;
              }

              if (!isPublic)
                setBoxesSlot(normalize.response.slots_data(message.racks));
              break;
            case TOPIC_FULLSCREEN.TROLLEY:
              const batchParcels = message.batchParcels ?? [];
              const boxParcels = message.boxParcels ?? [];
              const trolleyParcels = message.trolleyParcels ?? [];

              const filterByServicePointID = (dataType: any[]) =>
                dataType.filter(
                  (dataParcels: any) =>
                    dataParcels.sortingHub === trolleyServicePointID,
                );

              if (isPublic && location.pathname.includes("trolley")) {
                setUpcomingBatches(filterByServicePointID(batchParcels));
                setBoxes(filterByServicePointID(boxParcels));
                setTrolleys(filterByServicePointID(trolleyParcels));
              }

              if (!isPublic) {
                setUpcomingBatches && setUpcomingBatches(batchParcels);
                setBoxes && setBoxes(boxParcels);
                setTrolleys && setTrolleys(trolleyParcels);
              }
              break;
            default:
              eventSource?.close();
          }
        };

        eventSource.onerror = (error) => {
          console.error("SSE connection error:", error);
          eventSource?.close();
        };
      } catch (error) {
        console.error("Error setting up SSE:", error);
      }
    };

    if (!accessForbidden) setupSSE();

    return () => {
      if (eventSource) {
        console.warn("SSE connection closed");
        eventSource.close();
      }
    };
  }, [accessForbidden, trolleyServicePointID]);

  return {
    globalFilterState,
    filterObj,
    setFilterObj,
    upcomingBatches,
    setUpcomingBatches,
    racks,
    setRacks,
    trolleys,
    setTrolleys,
    boxesSlot,
    setBoxesSlot,
    boxes,
    setBoxes,
    fetchUpcomingBatches,
    fetchBoxes,
    fetchSlotsData,
    fetchTrolleys,
    TROLLEY_STATUS,
    accessForbidden,
    setAccessToForbidden,
    handleFetchPublicBoxList,
    handleFetchPublicTrolleyList,
  };
};

export default useDeliveryFullscreen;
